import React, { useMemo, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useListUsersQuery } from "~/shared/api/user/user.grpc";
import {
  useGetDriverActivitySeriesQuery,
  useGetEngineHoursSeriesQuery,
  useGetFuelConsumptionSeriesQuery,
  useGetRouteCategorySeriesQuery,
} from "~/shared/api/analytics/analytics.grpc";
import { fullName } from "~/shared/helpers";
import { DateValue, formatDurationShort } from "~/shared/lib/analytics-utils";
import { AnalyticsLineChart, SeriesPoint } from "./AnalyticsLineChart";
import { ChartEntitySelect } from "./ChartEntitySelect";

const CATEGORY_COLORS: Record<number, string> = {
  [ConnectProto.RouteCategory.CO]: "#0066FF",
  [ConnectProto.RouteCategory.MSW]: "#22C55E",
  [ConnectProto.RouteCategory.DISINFECTION]: "#8B5CF6",
  [ConnectProto.RouteCategory.TIPPER]: "#EF4444",
  [ConnectProto.RouteCategory.LIQUID]: "#EAB308",
};

const CATEGORY_LABEL_KEYS = {
  [ConnectProto.RouteCategory.CO]: "CATEGORY_CO",
  [ConnectProto.RouteCategory.MSW]: "CATEGORY_MSW",
  [ConnectProto.RouteCategory.DISINFECTION]: "CATEGORY_DISINFECTION",
  [ConnectProto.RouteCategory.TIPPER]: "CATEGORY_TIPPER",
  [ConnectProto.RouteCategory.LIQUID]: "CATEGORY_LIQUID",
} as const;

interface Props {
  startDate: DateValue;
  endDate: DateValue;
  routeCategory: ConnectProto.RouteCategory;
}

function toPoints(perDay?: { date?: string; value?: bigint }[]): SeriesPoint[] {
  return (perDay ?? []).map((p) => ({ date: p.date ?? "", value: Number(p.value ?? 0n) }));
}

const ChartBlock: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  subvalue?: string;
  children: React.ReactNode;
}> = ({ title, value, subtitle, subvalue, children }) => (
  <div className="flex flex-col">
    <p className="text-sm font-semibold">
      {title} <span className="text-blue-600">{value}</span>
    </p>
    {subtitle && (
      <p className="text-xs text-gray-500">
        {subtitle} <span className="text-blue-600">{subvalue}</span>
      </p>
    )}
    <div className="mt-2 h-[260px]">{children}</div>
  </div>
);

export const DriversSection: React.FC<Props> = ({ startDate, endDate, routeCategory }) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const [selectedDriver, setSelectedDriver] = useState<string>("all");

  const orgId = BigInt(selectedCompanyId || 0);
  const period = { from: startDate.toString(), to: endDate.toString() };
  const driverId = selectedDriver !== "all" ? BigInt(selectedDriver) : undefined;
  const skip = !selectedCompanyId;

  const { data: usersData } = useListUsersQuery(
    {
      ownerOrganizationIds: [orgId],
      roles: [ConnectProto.UserRole.DRIVER],
      statuses: [ConnectProto.UserStatus.ACTIVE],
    },
    { skip },
  );

  const activityArgs = { ownerOrganizationId: orgId, period, driverId, routeCategory };
  const shifts = useGetDriverActivitySeriesQuery(
    { ...activityArgs, metric: ConnectProto.DriverMetric.SHIFTS_COMPLETED },
    { skip },
  );
  const routes = useGetDriverActivitySeriesQuery(
    { ...activityArgs, metric: ConnectProto.DriverMetric.ROUTES_COMPLETED },
    { skip },
  );
  const reward = useGetDriverActivitySeriesQuery(
    { ...activityArgs, metric: ConnectProto.DriverMetric.REWARD },
    { skip },
  );
  const byCategory = useGetRouteCategorySeriesQuery(
    { ownerOrganizationId: orgId, period, driverId },
    { skip },
  );
  const fuel = useGetFuelConsumptionSeriesQuery(
    { ownerOrganizationId: orgId, period, driverId },
    { skip },
  );
  const engine = useGetEngineHoursSeriesQuery(
    { ownerOrganizationId: orgId, period, driverId },
    { skip },
  );

  const drivers = useMemo(
    () =>
      (usersData?.users ?? []).map((u) => ({
        id: String(u.id),
        name: fullName(u),
      })),
    [usersData],
  );

  const categorySeries = useMemo(
    () =>
      (byCategory.data?.series ?? []).map((s) => ({
        label: t(
          Namespace.Enum,
          CATEGORY_LABEL_KEYS[s.routeCategory as keyof typeof CATEGORY_LABEL_KEYS] ??
            "CATEGORY_UNDEFINED",
        ),
        color: CATEGORY_COLORS[s.routeCategory ?? 0] ?? "#9CA3AF",
        fill: false,
        perDay: toPoints(s.perDay),
      })),
    [byCategory.data, t],
  );

  const chart = (
    query: { isLoading: boolean; isFetching: boolean },
    perDay: SeriesPoint[],
    label: string,
    extra?: { valueSuffix?: string; formatValue?: (v: number) => string; total?: string },
  ) => (
    <AnalyticsLineChart
      series={[{ label, perDay }]}
      startDate={startDate}
      endDate={endDate}
      isLoading={query.isLoading || query.isFetching}
      {...extra}
    />
  );

  return (
    <div className="flex flex-col border rounded-xl bg-white md:h-full md:min-h-0 md:overflow-hidden">
      {/* Pinned header: the driver filter applies to every chart below. */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-100 p-4">
        <h3 className="text-lg text-gray-900 font-bold">
          {t(Namespace.Analytics, "drivers.section.title")}
        </h3>
        <ChartEntitySelect
          value={selectedDriver}
          options={drivers}
          allLabel={t(Namespace.Analytics, "charts.drivers.all")}
          onChange={setSelectedDriver}
        />
      </div>

      <div className="flex flex-col gap-8 p-4 md:min-h-0 md:flex-1 md:overflow-y-auto">
        <ChartBlock
          title={t(Namespace.Analytics, "charts.shifts.title")}
          value={String(Number(shifts.data?.total ?? 0n))}
        >
          {chart(shifts, toPoints(shifts.data?.perDay), t(Namespace.Analytics, "units.shifts"), {
            valueSuffix: t(Namespace.Analytics, "units.shifts"),
            total: `${Number(shifts.data?.total ?? 0n)} ${t(Namespace.Analytics, "units.shifts")}`,
          })}
        </ChartBlock>

        <ChartBlock
          title={t(Namespace.Analytics, "charts.routes.title")}
          value={String(Number(routes.data?.total ?? 0n))}
        >
          {chart(routes, toPoints(routes.data?.perDay), t(Namespace.Analytics, "units.routes"), {
            valueSuffix: t(Namespace.Analytics, "units.routes"),
            total: `${Number(routes.data?.total ?? 0n)} ${t(Namespace.Analytics, "units.routes")}`,
          })}
        </ChartBlock>

        <div className="flex flex-col">
          <p className="text-sm font-semibold">
            {t(Namespace.Analytics, "charts.routesByCategory.title")}
          </p>
          <div className="mt-1 flex flex-wrap gap-3">
            {categorySeries.map((s) => (
              <span key={s.label} className="flex items-center gap-1 text-xs text-gray-700">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.label}{" "}
                <span className="font-semibold" style={{ color: s.color }}>
                  {s.perDay.reduce((sum, p) => sum + p.value, 0)}
                </span>
              </span>
            ))}
          </div>
          <div className="mt-2 h-[260px]">
            <AnalyticsLineChart
              series={categorySeries}
              startDate={startDate}
              endDate={endDate}
              isLoading={byCategory.isLoading || byCategory.isFetching}
            />
          </div>
        </div>

        <ChartBlock
          title={t(Namespace.Analytics, "charts.reward.title")}
          value={`${Number(reward.data?.total ?? 0n)} ${t(Namespace.Analytics, "units.currency")}`}
        >
          {chart(
            reward,
            toPoints(reward.data?.perDay),
            t(Namespace.Analytics, "charts.reward.title"),
            {
              valueSuffix: t(Namespace.Analytics, "units.currency"),
              total: `${Number(reward.data?.total ?? 0n)} ${t(Namespace.Analytics, "units.currency")}`,
            },
          )}
        </ChartBlock>

        <ChartBlock
          title={t(Namespace.Analytics, "charts.fuelConsumption.title")}
          value={`${(fuel.data?.totalLitres ?? 0).toFixed(0)}${t(Namespace.Analytics, "units.litersShort")}`}
          subtitle={t(Namespace.Analytics, "charts.fuelConsumption.avgPer100")}
          subvalue={
            fuel.data?.avgLitresPer100km != null
              ? `${fuel.data.avgLitresPer100km.toFixed(1)}${t(Namespace.Analytics, "units.litersShort")}`
              : "—"
          }
        >
          <AnalyticsLineChart
            series={[
              {
                label: t(Namespace.Analytics, "charts.fuelConsumption.title"),
                perDay: (fuel.data?.perDay ?? []).map((p) => ({
                  date: p.date ?? "",
                  value: p.value ?? 0,
                })),
              },
            ]}
            startDate={startDate}
            endDate={endDate}
            isLoading={fuel.isLoading || fuel.isFetching}
            valueSuffix={t(Namespace.Analytics, "units.liters")}
            formatValue={(v) => v.toFixed(1)}
            total={`${(fuel.data?.totalLitres ?? 0).toFixed(0)} ${t(Namespace.Analytics, "units.liters")}`}
          />
        </ChartBlock>

        <ChartBlock
          title={t(Namespace.Analytics, "charts.engineHours.title")}
          value={formatDurationShort(Number(engine.data?.totalSeconds ?? 0n), t)}
          subtitle={t(Namespace.Analytics, "charts.engineHours.avgPerShift")}
          subvalue={
            engine.data?.avgSecondsPerShift != null
              ? formatDurationShort(Number(engine.data.avgSecondsPerShift), t)
              : "—"
          }
        >
          {chart(
            engine,
            toPoints(engine.data?.perDay).map((p) => ({ ...p, value: p.value / 3600 })),
            t(Namespace.Analytics, "charts.engineHours.title"),
            {
              formatValue: (v) => formatDurationShort(v * 3600, t),
              total: formatDurationShort(Number(engine.data?.totalSeconds ?? 0n), t),
            },
          )}
        </ChartBlock>
      </div>
    </div>
  );
};
